import { observer } from 'mobx-react-lite'
import { useOutletContext } from 'react-router'
import { type ProfileOutletContext } from '@/features/iam/users/Profile/ProfileContainer'
import { ProfileHeaderContainer } from '@/features/iam/users/components/ProfileHeader/ProfileHeaderContainer'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { useTask } from '@/hooks/task-hooks'
import { useActivation } from '@/hooks/view-model-hooks'
import { useDocumentTitle } from '@/hooks/document-title'
import { useFeatures } from '@/contexts/root-store-context'
import {
  type NotificationMedium,
  type NotificationTypeViewModel,
} from '@/features/iam/users/ProfileNotificationsViewModel'
import {
  MediumToggle,
  NotificationList,
  NotificationPreferenceItem,
  Notifications,
  SnoozeDropdown,
} from '@/features/iam/users/Profile/Notifications'

const media: NotificationMedium[] = ['Push', 'Email']

export default observer(function NotificationsContainer() {
  const { vm, scrollOffset } = useOutletContext<ProfileOutletContext>()
  useActivation(vm.notifications)
  useDocumentTitle('Notification Preferences')
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))
  return (
    <Notifications
      corporateMode={shellInfo.corporateMode}
      header={
        <ProfileHeaderContainer
          viewTitle="Notifications"
          vm={vm}
          scrollOffset={scrollOffset}
        />
      }
      isSnoozingEnabled={vm.notifications.isSnoozingEnabled}
    >
      {vm.notifications.fetch.match({
        pending: () => <ContentLoading />,
        rejected: (err) => <ContentError error={err} />,
        resolved: () => (
          <NotificationList
            masterItem={
              <NotificationPreferenceItemContainer
                type={vm.notifications.master}
                snoozingOn={vm.notifications.isSnoozingEnabled}
              />
            }
            items={vm.notifications.types.map((type) => (
              <NotificationPreferenceItemContainer
                type={type}
                key={type.id}
                snoozingOn={vm.notifications.isSnoozingEnabled}
              />
            ))}
          />
        ),
      })}
    </Notifications>
  )
})

interface NotificationPreferenceItemContainerProps {
  type: NotificationTypeViewModel
  snoozingOn?: boolean
}

const NotificationPreferenceItemContainer = observer(
  function NotificationPreferenceItemContainer({
    type,
    snoozingOn = false,
  }: NotificationPreferenceItemContainerProps) {
    return (
      <NotificationPreferenceItem
        title={type.title}
        description={type.description}
        anySnoozed={type.isAnyMediumSnoozed()}
        snoozingOn={snoozingOn}
        snoozeDropdown={<SnoozeDropdownContainer type={type} />}
        mediumToggles={media.map((medium) => (
          <MediumToggleContainer key={medium} type={type} medium={medium} />
        ))}
      />
    )
  },
)

interface MediumToggleContainerProps {
  type: NotificationTypeViewModel
  medium: NotificationMedium
}

const MediumToggleContainer = observer(function MediumToggleContainer({
  type,
  medium,
}: MediumToggleContainerProps) {
  const toggle = useTask.resolved(
    () => type.toggleMedium(medium),
    [type, medium],
  )
  return (
    <MediumToggle
      checked={type.isEnabled(medium)}
      onToggle={toggle}
      isPending={toggle.pending}
      canToggle={type.canToggleMedium(medium)}
    />
  )
})

interface SnoozeDropdownContainerProps {
  type: NotificationTypeViewModel
}

const SnoozeDropdownContainer = observer(function SnoozeDropdownContainer({
  type,
}: SnoozeDropdownContainerProps) {
  const snoozed = type.isAnyMediumSnoozed()

  return (
    <SnoozeDropdown
      snoozed={snoozed}
      snoozedUntil={snoozed ? type.mediumSnoozeDate('Email') : undefined}
      canSnooze={type.canSnooze()}
      isPending={type.snoozeFor.pending}
      onSnooze={type.snoozeFor}
    />
  )
})
